import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export default function Home() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState({
    users: 1250,
    boxes: 8500,
    satisfaction: 98
  });

  const [animatedStats, setAnimatedStats] = useState({
    users: 0,
    boxes: 0,
    satisfaction: 0
  });

  const [testimonials] = useState([
    {
      name: "Ayşe Yılmaz",
      role: "Ev Sahibi",
      comment: "Taşınma sırasında tüm kolilerimi dijital ortamda takip edebildim. Hangi kolide ne olduğunu anında bulabiliyorum. Harika bir sistem!",
      rating: 5,
      avatar: "https://public.readdy.ai/ai/img_res/aa53eb4ae86bd77edd0942bc8251c4fe.jpg"
    },
    {
      name: "Mehmet Demir",
      role: "İşletme Sahibi",
      comment: "Depomda binlerce koli var. padbaq sayesinde QR kod ile anında istediğim koliyi bulabiliyorum. Zaman tasarrufu inanılmaz!",
      rating: 5,
      avatar: "https://readdy.ai/api/search-image?query=Professional%20Turkish%20man%20smiling%20portrait%20headshot%20clean%20white%20background%20business%20casual%20attire%20confident%20friendly%20expression%20modern%20photography&width=80&height=80&seq=avatar002&orientation=squarish"
    },
    {
      name: "Zeynep Kaya",
      role: "Öğrenci",
      comment: "Yurt odasında yer sıkıntısı çekiyordum. Kolilerimi etiketleyip depoladım, ne nerede hepsini biliyorum. Çok pratik!",
      rating: 5,
      avatar: "https://readdy.ai/api/search-image?query=Professional%20Turkish%20woman%20smiling%20portrait%20headshot%20clean%20white%20background%20business%20casual%20attire%20confident%20friendly%20expression%20modern%20photography&width=80&height=80&seq=avatar003&orientation=squarish"
    },
    {
      name: "Can Özdemir",
      role: "E-ticaret Girişimci",
      comment: "Ürün stoklarımı koli bazında takip ediyorum. Hangi üründen kaç adet kaldığını anında görebiliyorum. Muhteşem!",
      rating: 5,
      avatar: "https://readdy.ai/api/search-image?query=Professional%20Turkish%20young%20man%20smiling%20portrait%20headshot%20clean%20white%20background%20business%20casual%20attire%20confident%20friendly%20expression%20modern%20photography&width=80&height=80&seq=avatar004&orientation=squarish"
    }
  ]);

  // Animate stats counter
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setAnimatedStats({
        users: Math.floor(stats.users * progress),
        boxes: Math.floor(stats.boxes * progress),
        satisfaction: Math.floor(stats.satisfaction * progress)
      });

      if (currentStep >= steps) {
        setAnimatedStats(stats);
        clearInterval(timer);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [stats]);

  useEffect(() => {
    if (user) {
      checkAdminStatus();
    }
  }, [user]);

  const checkAdminStatus = async () => {
    if (!user) return;
    
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('is_admin, email')
        .eq('id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          const { data: newProfile, error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: user.id,
              email: user.email,
              full_name: user.user_metadata?.full_name || '',
              phone: user.user_metadata?.phone || '',
              is_admin: user.email === 'fadiloglu@kalyonkentsel.com',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .select()
            .single();

          if (!insertError && newProfile) {
            setIsAdmin(newProfile.is_admin === true);
          }
        }
      } else if (profileData) {
        setIsAdmin(profileData.is_admin === true);
      }
    } catch (err) {
      console.error('Admin kontrolü yapılamadı:', err);
    }
  };

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Çıkış hatası:', error);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121C2E' }}>
      {/* Floating Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-purple-400/10 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-white/5 rounded-full blur-xl animate-pulse delay-2000"></div>
      </div>

      {/* Header with Navigation */}
      <header className="relative z-10 bg-[#1a2942]/80 backdrop-blur-md border-b border-white/10 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-white cursor-pointer" onClick={() => navigate('/')}>
                pad<span className="text-orange-500">baq</span>
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {user ? (
                <>
                  <button 
                    onClick={() => navigate('/dashboard')}
                    className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Panel
                  </button>
                  <button 
                    onClick={() => navigate('/boxes')}
                    className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Koli Yönetimi
                  </button>
                  <button 
                    onClick={() => navigate('/profile')}
                    className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Profil
                  </button>
                  {isAdmin && (
                    <button 
                      onClick={() => navigate('/admin')}
                      className="text-teal-400 hover:text-teal-300 px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-shield-star-line mr-2"></i>
                      Admin
                    </button>
                  )}
                  <button 
                    onClick={handleSignOut}
                    className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-logout-circle-line mr-2"></i>
                    Çıkış
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={() => navigate('/login')}
                    className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Giriş Yap
                  </button>
                  <button 
                    onClick={() => navigate('/login?register=true')}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    Kayıt Ol
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo */}
          <div className="mb-8">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-tight">
              pad<span className="text-orange-500">baq</span>
            </h1>
          </div>

          {/* Main Slogan */}
          <div className="mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Kolilerinizi 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400"> dijital ortamda </span>
              takip edin
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              QR kod teknolojisi ile eşyalarınızı organize edin, ne nerede anında bilin
            </p>
          </div>

          {/* Statistics Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-white/10">
              <div className="text-3xl font-bold text-purple-400 mb-2">{animatedStats.users.toLocaleString()}</div>
              <div className="text-sm text-white/70">Aktif Kullanıcı</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-white/10">
              <div className="text-3xl font-bold text-orange-400 mb-2">{animatedStats.boxes.toLocaleString()}</div>
              <div className="text-sm text-white/70">Takip Edilen Koli</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-white/10">
              <div className="text-3xl font-bold text-green-400 mb-2">%{animatedStats.satisfaction}</div>
              <div className="text-sm text-white/70">Memnuniyet</div>
            </div>
          </div>

          {/* Testimonials Section */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-8">Kullanıcılarımız Ne Diyor?</h3>
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 shadow-xl max-w-2xl mx-auto border border-white/10">
              <div className="flex items-center justify-center mb-6">
                <img 
                  src={testimonials[currentTestimonial].avatar}
                  alt={testimonials[currentTestimonial].name}
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div className="text-left">
                  <h4 className="font-semibold text-white">{testimonials[currentTestimonial].name}</h4>
                  <p className="text-white/70 text-sm">{testimonials[currentTestimonial].role}</p>
                </div>
              </div>
              <p className="text-white/90 text-lg italic mb-4">"{testimonials[currentTestimonial].comment}"</p>
              <div className="flex justify-center">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                ))}
              </div>
              <div className="flex justify-center mt-4 space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                      index === currentTestimonial ? 'bg-orange-500' : 'bg-white/30'
                    }`}
                    onClick={() => setCurrentTestimonial(index)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Main Feature Card */}
          <div className="mb-16">
            <div 
              className="group bg-gradient-to-br from-purple-500/20 to-orange-500/20 backdrop-blur-sm rounded-3xl p-12 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border border-white/20"
              onClick={() => user ? navigate('/boxes') : navigate('/login')}
            >
              <div className="relative overflow-hidden rounded-2xl mb-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20digital%20package%20tracking%20system%20with%20QR%20codes%20on%20organized%20storage%20boxes%20clean%20warehouse%20environment%20smartphone%20scanning%20technology%20inventory%20management%20professional%20photography%20bright%20lighting%20organized%20shelving%20units&width=800&height=400&seq=mainfeature001&orientation=landscape"
                  alt="Koli Yönetim Sistemi"
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-600/30 to-transparent"></div>
              </div>
              <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-orange-500 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-qr-code-line text-white text-4xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Kolilerinizi Dijital Ortamda Yönetin</h3>
              <p className="text-white/80 text-lg mb-6 max-w-2xl mx-auto">
                QR kod teknolojisi ile her kolinizi etiketleyin, içeriğini kaydedin ve anında bulun. 
                Taşınma, depolama veya organizasyon - her durumda yanınızda!
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-6">
                <div className="flex items-center text-white/90">
                  <i className="ri-qr-scan-2-line text-purple-400 text-2xl mr-2"></i>
                  <span>QR Kod ile Anında Erişim</span>
                </div>
                <div className="flex items-center text-white/90">
                  <i className="ri-search-line text-orange-400 text-2xl mr-2"></i>
                  <span>Gelişmiş Arama</span>
                </div>
                <div className="flex items-center text-white/90">
                  <i className="ri-price-tag-3-line text-green-400 text-2xl mr-2"></i>
                  <span>Etiket Sistemi</span>
                </div>
              </div>
              <button className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-lg font-semibold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap">
                {user ? 'Kolilerime Git' : 'Hemen Başla'}
              </button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-white/10">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-qr-code-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">QR Kod Sistemi</h3>
              <p className="text-white/70">Her koli için benzersiz QR kod oluşturun, telefonunuzla tarayın ve anında içeriğe ulaşın</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-white/10">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Akıllı Arama</h3>
              <p className="text-white/70">Koli adı, etiket veya içerik ile arama yapın, aradığınızı saniyeler içinde bulun</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border border-white/10">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-price-tag-3-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Etiket Yönetimi</h3>
              <p className="text-white/70">Kolilerinizi kategorilere ayırın, özel etiketler oluşturun ve organize edin</p>
            </div>
          </div>

          {/* Use Cases */}
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 mb-16 shadow-xl border border-white/10">
            <h3 className="text-3xl font-bold text-white mb-8">Kullanım Alanları</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="text-left bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-truck-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Taşınma</h4>
                    <p className="text-white/70 text-sm">Taşınırken hangi kolide ne olduğunu unutmayın. Tüm eşyalarınızı dijital ortamda takip edin.</p>
                  </div>
                </div>
              </div>

              <div className="text-left bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-archive-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Depolama</h4>
                    <p className="text-white/70 text-sm">Depodaki kolilerinizi organize edin, hangi rafta ne olduğunu anında öğrenin.</p>
                  </div>
                </div>
              </div>

              <div className="text-left bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-pink-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-store-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">E-ticaret</h4>
                    <p className="text-white/70 text-sm">Ürün stoklarınızı koli bazında yönetin, envanter takibini kolaylaştırın.</p>
                  </div>
                </div>
              </div>

              <div className="text-left bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-home-4-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Ev Organizasyonu</h4>
                    <p className="text-white/70 text-sm">Mevsimlik eşyalar, hobi malzemeleri - her şeyi düzenli tutun.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-12">Nasıl Çalışır?</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="relative">
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">1</div>
                <h4 className="text-xl font-semibold text-white mb-2">Kayıt Ol</h4>
                <p className="text-white/70">Ücretsiz hesap oluşturun ve hemen başlayın</p>
                <div className="hidden md:block absolute top-6 left-1/2 w-full h-0.5 bg-gradient-to-r from-purple-400/50 to-orange-400/50 transform translate-x-6"></div>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">2</div>
                <h4 className="text-xl font-semibold text-white mb-2">Koli Ekle</h4>
                <p className="text-white/70">Kolilerinizi sisteme ekleyin, içeriklerini kaydedin</p>
                <div className="hidden md:block absolute top-6 left-1/2 w-full h-0.5 bg-gradient-to-r from-orange-400/50 to-green-400/50 transform translate-x-6"></div>
              </div>
              <div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">3</div>
                <h4 className="text-xl font-semibold text-white mb-2">QR Kod ile Takip Et</h4>
                <p className="text-white/70">QR kodları yazdırın ve kolilerinizi anında bulun</p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-orange-500/20 to-purple-500/20 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-white/20">
            <h3 className="text-3xl font-bold text-white mb-4">Hemen Başlayın!</h3>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Kolilerinizi organize etmek hiç bu kadar kolay olmamıştı. Ücretsiz hesap oluşturun ve dijital organizasyonun keyfini çıkarın.
            </p>
            <button 
              onClick={() => navigate('/login?register=true')}
              className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-lg font-semibold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-user-add-line mr-2"></i>
              Ücretsiz Kayıt Ol
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-gradient-to-br from-orange-500/10 to-purple-500/10 backdrop-blur-md border-t border-white/10 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Description */}
            <div className="col-span-1 md:col-span-1">
              <h3 className="text-2xl font-bold text-white mb-4">
                pad<span className="text-orange-500">baq</span>
              </h3>
              <p className="text-white/70 text-sm mb-4">
                Kolilerinizi dijital ortamda takip edin. QR kod teknolojisi ile eşyalarınızı organize edin.
              </p>
              <div className="flex space-x-3">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-white text-lg"></i>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-white text-lg"></i>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-white text-lg"></i>
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-white text-lg"></i>
                </a>
              </div>
            </div>

            {/* Hızlı Linkler */}
            <div>
              <h4 className="text-white font-semibold text-lg mb-4">Hızlı Linkler</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">Anasayfa</a>
                </li>
                <li>
                  <a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">Hakkımızda</a>
                </li>
                <li>
                  <a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">Nasıl Çalışır?</a>
                </li>
                <li>
                  <a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">Blog</a>
                </li>
              </ul>
            </div>

            {/* Özellikler */}
            <div>
              <h4 className="text-white font-semibold text-lg mb-4">Özellikler</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">QR Kod Sistemi</a>
                </li>
                <li>
                  <a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">Koli Takibi</a>
                </li>
                <li>
                  <a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">Etiket Yönetimi</a>
                </li>
                <li>
                  <a href="#" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">Akıllı Arama</a>
                </li>
              </ul>
            </div>

            {/* İletişim */}
            <div>
              <h4 className="text-white font-semibold text-lg mb-4">İletişim</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-map-pin-line text-orange-400 text-lg mr-3 mt-1"></i>
                  <span className="text-white/70 text-sm">Maslak Mahallesi, Büyükdere Caddesi No:123, Sarıyer/İstanbul</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-phone-line text-orange-400 text-lg mr-3"></i>
                  <a href="tel:+902121234567" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">+90 (212) 123 45 67</a>
                </li>
                <li className="flex items-center">
                  <i className="ri-mail-line text-orange-400 text-lg mr-3"></i>
                  <a href="mailto:info@padbaq.com" className="text-white/70 hover:text-white transition-colors text-sm cursor-pointer">info@padbaq.com</a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-white/60 text-sm mb-4 md:mb-0">
                © 2024 padbaq. Tüm hakları saklıdır.
              </p>
              <div className="flex space-x-6">
                <a href="#" className="text-white/60 hover:text-white transition-colors text-sm cursor-pointer">Gizlilik Politikası</a>
                <a href="#" className="text-white/60 hover:text-white transition-colors text-sm cursor-pointer">Kullanım Koşulları</a>
                <a href="#" className="text-white/60 hover:text-white transition-colors text-sm cursor-pointer">KVKK</a>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors text-sm cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Chat Support Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button className="bg-gradient-to-r from-orange-500 to-orange-600 text-white w-14 h-14 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 flex items-center justify-center cursor-pointer">
          <i className="ri-chat-3-line text-xl"></i>
        </button>
      </div>
    </div>
  );
}
